import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Clock, Factory, ClipboardList, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STATUS_ORDER = { in_production: 0, staged: 1, scheduled: 2, completed: 3, finalized: 4 };

export default function MobileProductionPage() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [lineFilter, setLineFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work-orders`);
      const sorted = (res.data || []).sort((a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99));
      setWorkOrders(sorted);
    } catch (err) {
      console.error('Failed to fetch work orders:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkOrders();
    const interval = setInterval(fetchWorkOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchWorkOrders]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWorkOrders();
  };

  const filtered = workOrders.filter(wo => {
    if (statusFilter !== 'all' && wo.status !== statusFilter) return false;
    if (lineFilter !== 'all' && wo.production_line !== lineFilter) return false;
    return true;
  });

  const statusCounts = {
    all: workOrders.length,
    in_production: workOrders.filter(w => w.status === 'in_production').length,
    staged: workOrders.filter(w => w.status === 'staged').length,
    scheduled: workOrders.filter(w => w.status === 'scheduled').length,
    completed: workOrders.filter(w => w.status === 'completed' || w.status === 'finalized').length,
  };

  const statusLabel = (s) => {
    const map = { scheduled: 'Scheduled', staged: 'Staged', in_production: 'Running', completed: 'Done', finalized: 'Done' };
    return map[s] || s;
  };

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <div className="mobile-page-header">
        <div style={{ flex: 1 }}>
          <div className="mobile-page-title">Production</div>
          <div className="mobile-page-subtitle">{workOrders.length} work orders</div>
        </div>
        <button
          onClick={handleRefresh}
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#007aff', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status Filters */}
      <div className="mobile-filter-tabs">
        {[
          { key: 'all', label: `All (${statusCounts.all})` },
          { key: 'in_production', label: `Running (${statusCounts.in_production})` },
          { key: 'staged', label: `Staged (${statusCounts.staged})` },
          { key: 'scheduled', label: `Scheduled (${statusCounts.scheduled})` },
          { key: 'completed', label: `Done (${statusCounts.completed})` },
        ].map(f => (
          <button
            key={f.key}
            className={`mobile-filter-tab ${statusFilter === f.key ? 'active' : ''}`}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Line Filters */}
      <div className="mobile-filter-tabs">
        {['all', 'P1', 'P2', 'P3'].map(l => (
          <button
            key={l}
            className={`mobile-filter-tab ${lineFilter === l ? 'active' : ''}`}
            onClick={() => setLineFilter(l)}
          >
            {l === 'all' ? 'All Lines' : `Line ${l}`}
          </button>
        ))}
      </div>

      {/* Work Order Cards */}
      {filtered.length === 0 ? (
        <div className="mobile-empty-state">
          <Factory />
          <p>No work orders match your filters</p>
        </div>
      ) : (
        filtered.map(wo => {
          const progress = wo.planned_quantity > 0 ? ((wo.actual_quantity || 0) / wo.planned_quantity) * 100 : 0;
          const showProgress = wo.status === 'in_production' || wo.status === 'completed' || wo.status === 'finalized';
          return (
            <div
              key={wo.wo_id}
              className={`mobile-wo-card status-${wo.status}`}
              onClick={() => navigate(`/mobile/production/${wo.wo_id}`)}
            >
              <div className="mobile-wo-header">
                <div>
                  <div className="mobile-wo-number">{wo.wo_number} · Line {wo.production_line}</div>
                  <div className="mobile-wo-item-name">{wo.item_name}</div>
                </div>
                <span className={`mobile-status-badge ${wo.status}`}>
                  {statusLabel(wo.status)}
                </span>
              </div>
              <div className="mobile-wo-meta">
                <span className="mobile-wo-meta-item">
                  <Clock size={14} />
                  {wo.planned_start_time || 'TBD'} - {wo.planned_end_time || 'TBD'}
                </span>
                <span className="mobile-wo-meta-item">
                  <Package size={14} />
                  {showProgress ? `${wo.actual_quantity || 0} / ` : ''}{wo.planned_quantity} cases
                </span>
                {showProgress && (
                  <span className="mobile-wo-meta-item">
                    <ClipboardList size={14} />
                    {wo.pallets_completed || 0} pallets
                  </span>
                )}
              </div>
              {showProgress && (
                <div className="mobile-wo-progress">
                  <div className="mobile-wo-progress-bar">
                    <div
                      className={`mobile-wo-progress-fill ${progress >= 100 ? 'green' : progress > 50 ? 'blue' : 'orange'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <div className="mobile-wo-progress-text">
                    <span>{progress.toFixed(0)}%</span>
                    <span>{wo.actual_quantity || 0} / {wo.planned_quantity}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
